import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Check, 
  X, 
  ArrowUp,
  ArrowDown,
  HelpCircle
} from 'lucide-react';
import api from '../../services/api';

interface FAQ {
  id: number;
  question: string;
  answer: string;
  category: string;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface FAQFormData {
  question: string;
  answer: string;
  category: string;
  isActive?: boolean;
}

const faqSchema = yup.object({
  question: yup
    .string()
    .required('Question is required')
    .min(10, 'Question must be at least 10 characters')
    .max(500, 'Question cannot exceed 500 characters'),
  
  answer: yup
    .string()
    .required('Answer is required')
    .min(20, 'Answer must be at least 20 characters'),
  
  category: yup
    .string()
    .required('Category is required')
    .max(100, 'Category cannot exceed 100 characters'),
  
  isActive: yup
    .boolean()
    .optional()
}).required();

const FAQManagement: React.FC = () => {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingFaq, setEditingFaq] = useState<FAQ | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors }
  } = useForm({
    defaultValues: {
      question: '',
      answer: '',
      category: 'General',
      isActive: true
    }
  });

  useEffect(() => {
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/faqs');
      setFaqs(response.data);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      showMessage('error', 'Failed to fetch FAQs');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const onSubmit = async (data: FAQFormData) => {
    try {
      if (editingFaq) {
        // Update existing FAQ
        await api.put(`/admin/faqs/${editingFaq.id}`, data);
        showMessage('success', 'FAQ updated successfully');
      } else {
        // Create new FAQ
        await api.post('/admin/faqs', {
          ...data,
          displayOrder: faqs.length
        });
        showMessage('success', 'FAQ created successfully');
      }
      
      fetchFAQs();
      resetForm();
    } catch (error: any) {
      console.error('Error saving FAQ:', error);
      showMessage('error', error.response?.data?.error || 'Failed to save FAQ');
    }
  };

  const handleEdit = (faq: FAQ) => {
    setEditingFaq(faq);
    setValue('question', faq.question);
    setValue('answer', faq.answer);
    setValue('category', faq.category);
    setValue('isActive', faq.isActive);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) {
      return;
    }

    try {
      await api.delete(`/admin/faqs/${id}`);
      showMessage('success', 'FAQ deleted successfully');
      fetchFAQs();
    } catch (error) {
      console.error('Error deleting FAQ:', error);
      showMessage('error', 'Failed to delete FAQ');
    }
  };

  const handleMove = async (faq: FAQ, direction: 'up' | 'down') => {
    const currentIndex = faqs.findIndex(f => f.id === faq.id);
    if (currentIndex === -1) return;
    
    if (direction === 'up' && currentIndex === 0) return;
    if (direction === 'down' && currentIndex === faqs.length - 1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    const newFaqs = [...faqs];
    [newFaqs[currentIndex], newFaqs[newIndex]] = [newFaqs[newIndex], newFaqs[currentIndex]];

    // Update display order
    const faqOrders = newFaqs.map((faq, index) => ({
      id: faq.id,
      displayOrder: index
    }));

    try {
      await api.post('/admin/faqs/reorder', { faqOrders });
      setFaqs(newFaqs);
      showMessage('success', 'FAQ order updated');
    } catch (error) {
      console.error('Error reordering FAQs:', error);
      showMessage('error', 'Failed to reorder FAQs');
    }
  };

  const resetForm = () => {
    reset();
    setEditingFaq(null);
    setShowForm(false);
  };

  const toggleActive = async (faq: FAQ) => {
    try {
      await api.put(`/admin/faqs/${faq.id}`, {
        ...faq,
        isActive: !faq.isActive
      });
      showMessage('success', `FAQ ${faq.isActive ? 'deactivated' : 'activated'} successfully`);
      fetchFAQs();
    } catch (error) {
      console.error('Error toggling FAQ status:', error);
      showMessage('error', 'Failed to update FAQ status');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading FAQs...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">FAQ Management</h1>
          <p className="text-gray-600 mt-1">Manage frequently asked questions</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          {showForm ? 'Cancel' : 'Add FAQ'}
        </button>
      </div>

      {message && (
        <div className={`p-4 rounded-md ${
          message.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-800' 
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          {message.text}
        </div>
      )}

      {showForm && (
        <div className="card p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            {editingFaq ? 'Edit FAQ' : 'Add New FAQ'}
          </h2>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <label className="input-label">
                Question *
              </label>
              <input
                type="text"
                {...register('question')}
                className="input-field"
                placeholder="What is your question?"
              />
              {errors.question && (
                <p className="mt-1 text-sm text-red-600">{errors.question.message}</p>
              )}
            </div>

            <div>
              <label className="input-label">
                Answer *
              </label>
              <textarea
                {...register('answer')}
                rows={6}
                className="input-field"
                placeholder="Provide a detailed answer..."
              />
              {errors.answer && (
                <p className="mt-1 text-sm text-red-600">{errors.answer.message}</p>
              )}
            </div>

            <div>
              <label className="input-label">
                Category *
              </label>
              <input
                type="text"
                {...register('category')}
                className="input-field"
                placeholder="e.g., General, Pricing, Technical"
              />
              {errors.category && (
                <p className="mt-1 text-sm text-red-600">{errors.category.message}</p>
              )}
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                {...register('isActive')}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">
                Active (visible on public pages)
              </label>
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                className="btn-primary"
              >
                <Check className="h-4 w-4 mr-2" />
                {editingFaq ? 'Update FAQ' : 'Create FAQ'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="btn-outline"
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card">
        <div className="p-6 border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            All FAQs ({faqs.length})
          </h2>
        </div>

        {faqs.length === 0 ? (
          <div className="p-12 text-center">
            <HelpCircle className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">No FAQs yet. Create your first one!</p>
          </div>
        ) : (
          <div className="divide-y">
            {faqs.map((faq, index) => (
              <div
                key={faq.id}
                className="p-6 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() => handleMove(faq, 'up')}
                      disabled={index === 0}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move up"
                    >
                      <ArrowUp className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleMove(faq, 'down')}
                      disabled={index === faqs.length - 1}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move down"
                    >
                      <ArrowDown className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="badge-primary">
                            {faq.category}
                          </span>
                          <span className={`px-2 py-1 text-xs rounded ${
                            faq.isActive 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {faq.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                          {faq.question}
                        </h3>
                        <p className="text-gray-600 line-clamp-2">
                          {faq.answer}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleActive(faq)}
                      className="p-2 text-gray-400 hover:text-gray-600"
                      title={faq.isActive ? 'Deactivate' : 'Activate'}
                    >
                      {faq.isActive ? <Check className="h-5 w-5" /> : <X className="h-5 w-5" />}
                    </button>
                    <button
                      onClick={() => handleEdit(faq)}
                      className="p-2 text-blue-600 hover:text-blue-700"
                      title="Edit"
                    >
                      <Edit className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(faq.id)}
                      className="p-2 text-red-600 hover:text-red-700"
                      title="Delete"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FAQManagement;
